import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'; import { ICallConversationalDetails } from 'src/app/interfaces/icall-conversational-details';
import { ICallOverview } from 'src/app/interfaces/icall-overview';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { IObjectivesStatsResponse } from 'src/app/interfaces/iobjective-stats-response';
import { TooltipService } from 'src/app/services/general/tooltip.service';
import { ISkillsStatsResponse } from 'src/app/interfaces/iskills-stats-response';
import { IMessageAdherenceStatsResponse } from 'src/app/interfaces/imessage-adherence-stats-response';
import { IArticulationScoreStats } from 'src/app/interfaces/iarticulation-score-stats';
import { IMessageAdherenceStatsV2Response } from 'src/app/interfaces/imessage-adherence-stats-v2-response';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-call-conversational-details-side-panel',
  templateUrl: './call-conversational-details-side-panel.component.html',
  styleUrls: ['./call-conversational-details-side-panel.component.scss'],
  standalone: false
})
export class CallConversationalDetailsSidePanelComponent {
  @Input() call: ICallOverview | undefined;
  @Input() accountUuid: string = '';
  @Input() moduleName: string = '';
  @Input() moduleUuid: string = '';
  isExpanded = false;
  ctaName = "View All";
  promptNameMapping: { [key: string]: string } = {
    "SCORE_SKILLS": "Skills",
    "SCORE_OVERALL": "Overall Score",
    "SCORE_CUSTOMIZED": "Objectives",
    "MESSAGE_ADHERENCE": "Message Adherence",
    "MESSAGE_ADHERENCE_V2": "Message Adherence",
  };
  totalScore: number = 5;
  conversationalCallDetails: ICallConversationalDetails | undefined;
  isConversationalCallLoading: boolean = true;
  isSticky: boolean = false;
  objectivesStatsResponse: IObjectivesStatsResponse | undefined;
  skillsStatsResponse: ISkillsStatsResponse | undefined;
  messageAdherenceStatsResponse: IMessageAdherenceStatsResponse | undefined;
  messageAdherenceStatsV2Response: IMessageAdherenceStatsV2Response | undefined;
  isMessageAdherenceV2: boolean = false;
  translatedPrompt: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private reportingService: ReportingService,
    private responseTimeService: ResponseTimeService,
    private dialogRef: MatDialogRef<CallConversationalDetailsSidePanelComponent>,
    private tooltipService: TooltipService,
    private translocoService: TranslocoService
  ) { }
  ngOnInit() {
    this.call = this.data.call;
    this.accountUuid = this.data.accountUuid;
    this.moduleName = this.data.moduleName;
    this.moduleUuid = this.data.moduleUuid;
    this.getConversationalCallDetail();
    this.ctaName = this.translocoService.translate('VIEW_ALL_LABEL', {} ,'reporting');
  }

  onScroll(event: any) {
    const offsetTop = event.target.scrollTop;
    this.isSticky = offsetTop > 74;
  }

  getConversationalCallDetail() {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getConversationalCallDetail(
          this.accountUuid,
          this.call?.callUuid || '',
          this.moduleUuid
        ),
        'getConversationalCallDetail'
      )
      .subscribe((response: ICallConversationalDetails) => {
        this.isConversationalCallLoading = false;
        this.conversationalCallDetails = response;
        this.translatedPrompt = this.translatePromptName(response?.promptNames);
        this.sortPromptNames();
        response?.promptNames.forEach((promptName) => {
          if (promptName === 'SCORE_SKILLS') {
            this.getConversationalCallSkillsStats();
          } else if (promptName === 'SCORE_OVERALL') {
            this.getConversationalCallMessageAdherenceStats();
          }
          else if (promptName === 'SCORE_CUSTOMIZED') {
            this.getConversationalCallObjectivesStats();
          }
          else if (promptName === 'MESSAGE_ADHERENCE_V2') {
            this.isMessageAdherenceV2 = true;
            this.getConversationalCallMessageAdherenceV2Stats();
          }
        })
      });
  }

  translatePromptName(promptNames: string[]): string[] {
    if (!promptNames) return [];
    return promptNames.map(
      (name) =>
        typeof name === 'string'
          ? name.toUpperCase().replace(/\s+/g, '_')
          : name
    );
  }


  sortPromptNames(): void {
    const predefinedOrder = ["SCORE_OVERALL", "SCORE_CUSTOMIZED", this.isMessageAdherenceV2 ? "MESSAGE_ADHERENCE_V2" : "MESSAGE_ADHERENCE", "SCORE_SKILLS"];
    this.conversationalCallDetails?.promptNames.sort((a: string, b: string) => {
      return predefinedOrder.indexOf(a) - predefinedOrder.indexOf(b);
    });
  }

  getConversationalCallObjectivesStats() {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getConversationalCallObjectivesStats(
          this.accountUuid,
          this.call?.callUuid || '',
          this.moduleUuid
        ),
        'getConversationalCallObjectivesStats'
      )
      .subscribe((response: IObjectivesStatsResponse) => {
        this.objectivesStatsResponse = response;
      });
  }

  getConversationalCallMessageAdherenceStats() {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getConversationalCallMessageAdherenceStats(
          this.accountUuid,
          this.call?.callUuid || '',
          this.moduleUuid
        ),
        'getConversationalCallSkillsStats'
      )
      .subscribe((response: IMessageAdherenceStatsResponse) => {
        if (response && response.articulationScoreStats) {
          // Convert articulationProgress to Map if necessary
          response.articulationScoreStats.forEach((stat: IArticulationScoreStats) => {
            if (!(stat.articulationProgress instanceof Map)) {
              stat.articulationProgress = new Map(
                Object.entries(stat.articulationProgress)
              );
            }
          });
        }
        this.messageAdherenceStatsResponse = response;
      });
  }

  getConversationalCallSkillsStats() {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getConversationalCallSkillsStats(
          this.accountUuid,
          this.call?.callUuid || '',
          this.moduleUuid
        ),
        'getConversationalCallSkillsStats'
      )
      .subscribe((response: ISkillsStatsResponse) => {
        this.skillsStatsResponse = response;
      });
  }

  getConversationalCallMessageAdherenceV2Stats() {
    this.responseTimeService.logResponseTime(
      this.reportingService.getConversationalCallMessageAdherenceV2Stats(
        this.accountUuid,
        this.call?.callUuid || '',
        this.moduleUuid
      ), 'getConversationalCallMessageAdherenceV2Stats'
    ).subscribe((response: IMessageAdherenceStatsV2Response) => {
      this.messageAdherenceStatsV2Response = response;
      console.log('Message Adherence V2 Response:', response);
    } )
  }

  toggleDescription() {
    this.isExpanded = !this.isExpanded;
    this.ctaName = this.isExpanded ? this.translocoService.translate('MINIMIZE_LABEL', {}, 'reporting') : this.translocoService.translate('VIEW_ALL_LABEL', {}, 'reporting');
  }

  close(): void {
    this.dialogRef.close();
  }

  calculateProgress(progress: number, total: number): any {
    if (progress < 0) progress = 0;
    if (progress > total) progress = total;
    let filled = Math.floor(progress);
    return { filled: filled, partial: progress - filled };
  }

  calculateProgressFromPercentage(percentage: number, total: number) {
    const progress = (percentage / 100) * total;
    return this.calculateProgress(progress, total);
  }

  openTooltip(event: MouseEvent, direction: string, content: string, width: number, height: number): void {
    let heading = '';
    let paragraph = '';
    if (content === 'call_type') {
      heading = this.translocoService.translate('CONVERSATIONAL_CALL', {}, 'reporting');
      paragraph = this.translocoService.translate('CONVERSATIONAL_CALL_TOOLTIP', {}, 'reporting');
    }
    if (content === 'session') {
      heading = this.translocoService.translate('TOTAL_SESSIONS', {}, 'reporting');
      paragraph = this.translocoService.translate('TOTAL_SESSION_TOOLTIP', {}, 'reporting');;
    }

    this.tooltipService.openTooltip(event, direction, heading, paragraph, width, height);
  }

  calculateAdjustedWidth(skillProgress: any[], index: number): number {
    const originalPercentages = skillProgress.map((p) => p.progressStatus.percentage);
    const totalPercentage = originalPercentages.reduce((sum, p) => sum + p, 0);

    if (totalPercentage <= 100) {
      return originalPercentages[index]; // No adjustment needed
    }

    // Calculate excess to be adjusted
    let excess = totalPercentage - 100;
    const adjustedPercentages = [...originalPercentages];

    for (let i = 0; i < adjustedPercentages.length; i++) {
      if (excess === 0) break;

      const decrement = Math.min(excess, adjustedPercentages[i]);
      adjustedPercentages[i] -= decrement;
      excess -= decrement;
    }

    return adjustedPercentages[index];
  }

}
